import { colors, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { ArrowBack, CheckCircle, WarningFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import React from 'react';
import { observer } from 'mobx-react-lite';
import ICensusReviewPageStore, { CensusReviewDomainParams } from 'store/modules/census/iCensusReviewPageStore';
import {
  BackButtonStyled,
  CaptionStyled,
  HeaderWrapperStyled,
  SpinnerWrapperStyled,
  TitleStyled,
  TitleWrapperStyled,
} from './styles';
import { HelpButton } from '../../HelpButton';
import {
  PAGE_HEADER_BACK_BUTTON_TEST_ID,
  PAGE_HEADER_ERROR_HEADER_TEST_ID,
  PAGE_HEADER_SUCCESS_HEADER_TEST_ID,
  PAGE_HEADER_LOADING_HEADER_TEST_ID,
  PAGE_HEADER_INCOMPATIBILITY_ERROR_HEADER_TEST_ID,
} from '../constants';

interface PageHeaderProps {
  title: string;
  caption: string;
  icon: React.ReactNode;
}

interface CensusStatsProps {
  pageStore: ICensusReviewPageStore<CensusReviewDomainParams>;
}

const Header: React.FC<PageHeaderProps> = ({ title, caption, icon }) => {
  return (
    <TitleWrapperStyled>
      {icon}
      <TitleStyled>{title}</TitleStyled>
      <CaptionStyled>{caption}</CaptionStyled>
      <HelpButton />
    </TitleWrapperStyled>
  );
};

const LoadingHeader: React.FC = () => {
  return (
    <TitleWrapperStyled data-test-id={PAGE_HEADER_LOADING_HEADER_TEST_ID}>
      <SpinnerWrapperStyled>
        <OverlaySpinner size="4rem" display overlay="none" />
      </SpinnerWrapperStyled>
      <TitleStyled>Census Processing</TitleStyled>
      <CaptionStyled>Results will be available in couple seconds</CaptionStyled>
      <HelpButton />
    </TitleWrapperStyled>
  );
};

const PageHeader: React.FC<CensusStatsProps> = observer(({ pageStore }) => {
  const { isLoading, hasErrors, incompatibilityErrorMsg, onBackRedirect } = pageStore;

  const renderResultHeader = (): React.ReactNode => {
    if (incompatibilityErrorMsg) {
      return (
        <div data-test-id={PAGE_HEADER_INCOMPATIBILITY_ERROR_HEADER_TEST_ID}>
          <Header
            title="Fix Issues With Import"
            caption="Please review the issues below and upload a corrected file."
            icon={<WarningFilled width="5.2rem" color={colors.red[600]} />}
          />
        </div>
      );
    }

    return hasErrors || incompatibilityErrorMsg ? (
      <div data-test-id={PAGE_HEADER_ERROR_HEADER_TEST_ID}>
        <Header
          title="Fix Issues With Import"
          caption="We've highlighted any errors in red below. You can make any updates or fixes on the Census and upload it again."
          icon={<WarningFilled width="5.2rem" color={colors.orange[500]} />}
        />
      </div>
    ) : (
      <div data-test-id={PAGE_HEADER_SUCCESS_HEADER_TEST_ID}>
        <Header
          title="Import Success!"
          caption="You can now submit the Census."
          icon={<CheckCircle width="5.2rem" color={colors.green[600]} />}
        />
      </div>
    );
  };

  return (
    <HeaderWrapperStyled>
      <BackButtonStyled
        leftIconProp={<ArrowBack />}
        type="text"
        size="medium"
        onClick={onBackRedirect}
        data-test-id={PAGE_HEADER_BACK_BUTTON_TEST_ID}
      >
        Back
      </BackButtonStyled>
      {isLoading ? <LoadingHeader /> : renderResultHeader()}
    </HeaderWrapperStyled>
  );
});

export default PageHeader;
